/* The agent's schema on the dot canvas: a card per table with its columns, their types and
   rules, a wire from each link column to the table it links to, and what the agent may do in
   each table. Tables sit in columns by how far their links reach: a table that links to none
   on the left, the tables linking to it to its right. */
import React, { useLayoutEffect, useMemo, useRef, useState } from "react";
import { Asterisk, KeyRound, Link2, Lock } from "lucide-react";

import PixelAvatar from "@/shared/components/PixelAvatar";
import { cn } from "@/shared/lib/cn";

import { ICONS } from "./table/columns";
import TableTile from "./TableTile";
import { ViewSwitch, type SpaceView } from "./TableToolbar";

import type { Agent, SpaceTable } from "@/shared/types";

const CAN: Record<SpaceTable["agent_can"], string> = {
  read: "read only",
  add: "add",
  update: "update",
  add_update: "add · update",
};

/** Each table's column on the canvas: one past the furthest table it links to. */
function ranks(tables: SpaceTable[]): Map<string, number> {
  const rank = new Map<string, number>();
  const visit = (table: SpaceTable, seen: Set<string>): number => {
    if (rank.has(table.id)) return rank.get(table.id) as number;
    if (seen.has(table.id)) return 0; // a cycle of links: drawn where it was met
    seen.add(table.id);
    const targets = table.columns
      .filter((column) => column.type === "link" && column.options.table !== table.id)
      .map((column) => tables.find((t) => t.id === column.options.table))
      .filter((t): t is SpaceTable => !!t);
    const value = targets.length ? Math.max(...targets.map((t) => visit(t, seen) + 1)) : 0;
    rank.set(table.id, value);
    return value;
  };
  tables.forEach((table) => visit(table, new Set()));
  return rank;
}

export default function SchemaView({ agent, tables, onOpen, onView }: {
  agent: Agent;
  tables: SpaceTable[];
  onOpen: (tableId: string) => void;
  onView: (view: SpaceView) => void;
}) {
  const canvas = useRef<HTMLDivElement>(null);
  const [wires, setWires] = useState<{ d: string; ends: [number, number][] }[]>([]);
  // memoised: the wires are drawn again whenever the layout's columns change, and a new
  // array every render would redraw them forever
  const own = useMemo(() => tables.filter((table) => !table.system), [tables]);
  const columns = useMemo(() => {
    const rank = ranks(own);
    const grouped: SpaceTable[][] = [];
    for (const table of own) (grouped[rank.get(table.id) ?? 0] ??= []).push(table);
    const people = tables.find((table) => table.system);
    if (people) (grouped[0] ??= []).push(people);
    return grouped.filter(Boolean);
  }, [own, tables]);

  // The wires are drawn once the cards are laid out, and again whenever they move.
  useLayoutEffect(() => {
    const root = canvas.current;
    if (!root) return;
    const draw = () => {
      const box = root.getBoundingClientRect();
      const drawn = [];
      for (const field of Array.from(root.querySelectorAll<HTMLElement>("[data-links-to]"))) {
        const target = root.querySelector<HTMLElement>(`[data-table="${field.dataset.linksTo}"]`);
        if (!target) continue;
        const from = field.getBoundingClientRect();
        const to = target.getBoundingClientRect();
        const rightward = to.left > from.right;
        const x1 = (rightward ? from.right : from.left) - box.left + root.scrollLeft;
        const y1 = from.top + from.height / 2 - box.top + root.scrollTop;
        const x2 = (rightward ? to.left : to.right) - box.left + root.scrollLeft;
        const y2 = to.top + 18 - box.top + root.scrollTop;
        const bend = Math.max(40, Math.abs(x2 - x1) / 2) * (rightward ? 1 : -1);
        drawn.push({ d: `M${x1} ${y1} C ${x1 + bend} ${y1}, ${x2 - bend} ${y2}, ${x2} ${y2}`, ends: [[x1, y1], [x2, y2]] as [number, number][] });
      }
      setWires(drawn);
    };
    draw();
    const observer = new ResizeObserver(draw);
    observer.observe(root);
    return () => observer.disconnect();
  }, [columns]);

  return (
    <div className="flex-1 min-w-0 min-h-0 flex flex-col">
      <div className="h-12 shrink-0 flex items-center gap-2 pl-5 pr-4 border-b hairline">
        <span className="text-[13.5px] font-medium text-[var(--text-primary)]">{agent.name}'s schema</span>
        <span className="text-[12.5px] text-[var(--text-tertiary)]">
          <span className="font-mono">{own.length}</span> tables ·{" "}
          <span className="font-mono">{own.reduce((n, t) => n + t.columns.filter((c) => c.type === "link").length, 0)}</span> links
        </span>
        <span className="flex-1" />
        <ViewSwitch view="schema" onView={onView} />
      </div>
      <div ref={canvas} className="relative flex-1 min-h-0 overflow-auto wf-dots bg-[var(--canvas)]">
        <svg aria-hidden="true" className="absolute inset-0 pointer-events-none overflow-visible" width="100%" height="100%">
          {wires.map((wire, index) => (
            <g key={index}>
              <path d={wire.d} fill="none" stroke="var(--text-quaternary)" strokeWidth={1.25} />
              {wire.ends.map(([x, y], end) => <circle key={end} cx={x} cy={y} r={3} fill="var(--canvas)" stroke="var(--text-quaternary)" strokeWidth={1.25} />)}
            </g>
          ))}
        </svg>
        <div className="relative flex items-start gap-12 p-10 w-max">
          {columns.map((group, index) => (
            <div key={index} className="flex flex-col gap-10 w-[236px]">
              {group.map((table) => <TableCard key={table.id} table={table} tables={tables} onOpen={() => onOpen(table.id)} />)}
              {index === Math.min(1, columns.length - 1) && own.length > 0 && <AgentCard agent={agent} tables={own} />}
            </div>
          ))}
        </div>
        <div className="sticky left-5 bottom-4 mt-auto inline-flex items-center gap-3.5 ml-5 mb-4 px-3 py-2 rounded-[12px] bg-[var(--surface)] ring-1 ring-[var(--hairline-strong)] shadow-lift text-[12px] text-[var(--text-tertiary)]">
          <span className="inline-flex items-center gap-1.5"><KeyRound className="w-3 h-3" strokeWidth={1.75} /> Unique</span>
          <span className="inline-flex items-center gap-1.5"><Asterisk className="w-3 h-3" strokeWidth={1.75} /> Required</span>
          <span className="inline-flex items-center gap-1.5"><Link2 className="w-3 h-3" strokeWidth={1.75} /> Links to a row</span>
          <span className="inline-flex items-center gap-1.5"><Lock className="w-3 h-3" strokeWidth={1.75} /> Kept by Definable</span>
        </div>
      </div>
    </div>
  );
}

function TableCard({ table, tables, onOpen }: { table: SpaceTable; tables: SpaceTable[]; onOpen: () => void }) {
  const named = (key: string) => table.columns.find((column) => column.key === key)?.name ?? key;
  return (
    <div data-table={table.id} className="rounded-[16px] bg-[var(--surface-2)] p-1 ring-1 ring-[var(--hairline)]">
      <button type="button" onClick={onOpen} className="press w-full h-[34px] flex items-center gap-2 px-2.5 pb-0.5 rounded-[12px] text-left hover:bg-[var(--accent-hover)]">
        <TableTile system={table.system} />
        <span className="flex-1 min-w-0 truncate text-[13px] font-medium text-[var(--text-primary)]">{table.name}</span>
        {table.system && <Lock className="w-3 h-3 text-[var(--text-quaternary)]" strokeWidth={2} />}
        {table.row_count !== null && <span className="text-[11.5px] font-mono text-[var(--text-quaternary)]">{table.row_count.toLocaleString("en-US")}</span>}
      </button>
      <div className="rounded-[12px] bg-[var(--surface)] ring-1 ring-[var(--hairline)] shadow-lift py-1">
        {table.columns.map((column, index) => {
          const Icon = ICONS[column.type];
          const target = column.type === "link" ? tables.find((t) => t.id === column.options.table) : undefined;
          return (
            <div
              key={column.key}
              data-links-to={target?.id}
              className={cn("flex items-center gap-2 h-7 px-2.5 text-[12.5px]", index === 0 ? "text-[var(--text-primary)] font-medium" : "text-[var(--text-secondary)]")}
            >
              <Icon className="w-3.5 h-3.5 shrink-0 text-[var(--text-quaternary)]" strokeWidth={1.75} />
              <span className="truncate">{column.name}</span>
              {column.rules.includes("unique") && <KeyRound aria-label="unique" className="w-3 h-3 shrink-0 text-[var(--text-tertiary)]" strokeWidth={1.75} />}
              {column.rules.includes("required") && <Asterisk aria-label="required" className="w-3 h-3 shrink-0 text-[var(--text-tertiary)]" strokeWidth={1.75} />}
              <span className="ml-auto shrink-0 text-[11px] text-[var(--text-quaternary)]">
                {target ? `→ ${target.name}` : column.type.replace("_", " ")}
              </span>
            </div>
          );
        })}
      </div>
      {table.unique_together.length > 0 && (
        <div className="flex items-center gap-1.5 px-2.5 pt-1.5 pb-1 text-[11.5px] text-[var(--text-tertiary)]">
          <KeyRound className="w-3 h-3 shrink-0" strokeWidth={1.75} />
          Unique together: {table.unique_together.map((keys) => keys.map(named).join(" + ")).join("; ")}
        </div>
      )}
      {table.system && (
        <div className="flex items-center gap-1.5 px-2.5 pt-1.5 pb-1 text-[11.5px] text-[var(--text-tertiary)]">
          <Lock className="w-3 h-3 shrink-0" strokeWidth={1.75} /> Everyone who talks to the agent
        </div>
      )}
    </div>
  );
}

function AgentCard({ agent, tables }: { agent: Agent; tables: SpaceTable[] }) {
  return (
    <div className="rounded-[16px] bg-[var(--surface-2)] p-1 ring-1 ring-[var(--hairline)]">
      <div className="h-[34px] flex items-center gap-2 px-2.5 pb-0.5">
        <PixelAvatar seed={agent.seed} hue={agent.hue} size={18} />
        <span className="text-[13px] font-medium text-[var(--text-primary)] truncate">What {agent.name} may do</span>
      </div>
      <div className="rounded-[12px] bg-[var(--surface)] ring-1 ring-[var(--hairline)] shadow-lift py-1.5">
        {tables.map((table) => (
          <div key={table.id} className={cn("flex items-center gap-2 h-[26px] px-2.5 text-[12.5px]", table.agent_can === "read" ? "text-[var(--text-quaternary)]" : "text-[var(--text-secondary)]")}>
            <TableTile size={13} />
            <span className="truncate">{table.name}</span>
            <span className="ml-auto text-[11.5px] text-[var(--text-tertiary)]">{CAN[table.agent_can]}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
